import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import Battery3BarOutlinedIcon from '@mui/icons-material/Battery3BarOutlined';
import BluetoothOutlinedIcon from '@mui/icons-material/BluetoothOutlined';
import CameraEnhanceOutlinedIcon from '@mui/icons-material/CameraEnhanceOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ContactlessOutlinedIcon from '@mui/icons-material/ContactlessOutlined';
import DialpadOutlinedIcon from '@mui/icons-material/DialpadOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';

import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SdCardOutlinedIcon from '@mui/icons-material/SdCardOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SettingsEthernetOutlinedIcon from '@mui/icons-material/SettingsEthernetOutlined';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import SpeakerPhoneOutlinedIcon from '@mui/icons-material/SpeakerPhoneOutlined';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import type { SvgIconProps } from '@mui/material';
import { useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { MutableRefObject, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type {
  DeviceResourceDiagnosticsTestResult,
  DiagnosticTestModule,
  DiagnosticTestStatus,
} from '../../domain/models/DeviceDiagnosticsDetails.model';
import { enumDiagnosticTestModule, enumDiagnosticTestStatus } from '../../domain/models/DeviceDiagnosticsDetails.model';
import type {
  DeviceDiagnosticsDetailGridProps,
  UseLogicalDeviceDiagnosticsDetailsPageColumns,
} from './DeviceDiagnosticDetailsPage.model';

export function useLogicalDeviceDiagnosticsDetailsPageColumns(): UseLogicalDeviceDiagnosticsDetailsPageColumns {
  const { t } = useTranslation(ALL_LNGS_NAMESPACES.LOGICAL_DEVICE);
  const theme = useTheme();

  const statusIconMap: Record<DiagnosticTestStatus, React.ComponentType<SvgIconProps>> = useMemo(
    () => ({
      [enumDiagnosticTestStatus.PASSED]: CheckCircleOutlinedIcon,
      [enumDiagnosticTestStatus.WARNING]: WarningAmberOutlinedIcon,
      [enumDiagnosticTestStatus.FAILED]: CancelOutlinedIcon,
      [enumDiagnosticTestStatus.NOT_APPLICABLE]: DoNotDisturbAltOutlinedIcon,
    }),
    [],
  );

  const moduleIconMap: Record<DiagnosticTestModule, JSX.Element> = useMemo(
    () => ({
      [enumDiagnosticTestModule.AUDIO]: <SpeakerPhoneOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.BATTERY]: <Battery3BarOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.BLUETOOTH]: <BluetoothOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.CAMERA_BACK]: <CameraEnhanceOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.CAMERA_FRONT]: <CameraEnhanceOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.CELLULAR]: <SensorsOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.CERTIFICATES]: <CheckBoxIcon fontSize="small" />,
      [enumDiagnosticTestModule.DISPLAY]: <StayCurrentPortraitIcon fontSize="small" />,
      [enumDiagnosticTestModule.ETHERNET]: <SettingsEthernetOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.KEYBOARD]: <DialpadOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.LED]: <LightbulbOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.MAG_STRIPE_READER]: <SimCardOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.NFC_READER]: <ContactlessOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.PRINTER]: <PrintOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.SMART_CARD_READER]: <SdCardOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.VIRTUAL_KEYBOARD]: <KeyboardOutlinedIcon fontSize="small" />,
      [enumDiagnosticTestModule.WIFI]: <WifiOutlinedIcon fontSize="small" />,
    }),
    [],
  );

  const colorStatusIcon = useCallback(
    (status: DiagnosticTestStatus): string => {
      switch (status) {
        case enumDiagnosticTestStatus.PASSED: {
          return theme.palette.success.main;
        }
        case enumDiagnosticTestStatus.FAILED: {
          return theme.palette.error.main;
        }
        case enumDiagnosticTestStatus.WARNING: {
          return theme.palette.warning.main;
        }
        case enumDiagnosticTestStatus.NOT_APPLICABLE: {
          return theme.palette.grey[300];
        }
        default: {
          return theme.palette.grey[300];
        }
      }
    },
    [theme],
  );

  const renderStatus = useCallback(
    (params: GridRenderCellParams, status: DiagnosticTestStatus): ReactNode => {
      const Icon = statusIconMap[status];
      const color = colorStatusIcon(params.value === 0 ? enumDiagnosticTestStatus.NOT_APPLICABLE : status);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color }}>
          <Icon fontSize="small" />
          <span>{params.value}</span>
        </div>
      );
    },
    [colorStatusIcon, statusIconMap],
  );

  const renderIconCellValue = useCallback(
    (params: GridRenderCellParams): ReactNode => {
      const raw = params.value as DiagnosticTestModule;
      const label = t(`modules.${raw}`, {
        ns: ALL_LNGS_NAMESPACES.ERROR_DIAGNOSTICS,
      });
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {moduleIconMap[raw]}
          <span>{label}</span>
        </div>
      );
    },
    [moduleIconMap, t],
  );

  const DEVICE_DIAGNOSTICS_COLUMNS = useMemo<GridColDef[]>(
    () => [
      {
        field: 'testResults.module',
        headerName: t('deviceDiagnostics.fields.testResults', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 3,
        renderCell: renderIconCellValue,
        valueGetter: (_, row: DeviceResourceDiagnosticsTestResult) => row.module,
        filterable: false,
        sortable: false,
      },
      {
        field: 'result',
        headerName: t('deviceDiagnostics.fields.result', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: (params: GridRenderCellParams<DeviceResourceDiagnosticsTestResult>) =>
          renderStatus({ ...params }, params.row.status),
        filterable: false,
        sortable: false,
      },
    ],
    [renderIconCellValue, renderStatus, t],
  );

  const defaultAvailableColumns = useMemo(
    () => DEVICE_DIAGNOSTICS_COLUMNS.map((col) => col.field),
    [DEVICE_DIAGNOSTICS_COLUMNS],
  );

  return {
    columns: DEVICE_DIAGNOSTICS_COLUMNS,
    defaultAvailableColumns,
  };
}

export function useInitialSetup() {
  const { t, ready: isTranslationReady } = useTranslation([
    ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
    ALL_LNGS_NAMESPACES.ERROR_DIAGNOSTICS,
  ]);
  const datagridRef = useGridApiRef();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedTab = searchParams.get('selectedTab');
  const theme = useTheme();

  return { t, datagridRef, navigate, location, searchParams, selectedTab, isTranslationReady, theme };
}

export function useGridPropsSetup({
  handleRowClick,
  datagridRef,
  total,
  getRowId,
  isLoading,
  shouldDisplayGrid,
}: {
  readonly handleRowClick: ({ row }: GridRowParams<DeviceResourceDiagnosticsTestResult>) => void;
  readonly datagridRef: MutableRefObject<GridApiPremium>;
  readonly total: number;
  readonly getRowId: (element: DeviceResourceDiagnosticsTestResult) => string;
  readonly isLoading: boolean;
  readonly shouldDisplayGrid: boolean;
}) {
  return useMemo<DeviceDiagnosticsDetailGridProps>(
    () => ({
      handleRowClick,
      total,
      getRowId,
      isLoading,
      shouldDisplayGrid,
      datagridRef,
    }),
    [handleRowClick, datagridRef, total, getRowId, isLoading, shouldDisplayGrid],
  );
}
